import { Button, DatePicker, Input, Select } from "antd";
import { TableProps } from "antd/es/table";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import DateIcon from "../../../assets/icons/DateIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import ReportRequestModal from "../../../components/Report/ReportRequestModal";
import CustomTable from "../../../components/ui/CustomTable";
import Loader from "../../../components/ui/Loader";
import Empty from "../../../components/Visit/EmptyVisit";
import {
  dateFormat,
  dbDateFormat,
  visitSearchTypeList,
} from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetBusinessDataQuery } from "../../../features/employee/employeeApi";
import { useGetVisitReportMutation } from "../../../features/report/reportApi";
import { useGetTeamVisitListQuery } from "../../../features/visit/visitApi";
import useDebounce from "../../../hooks/useDebounce";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { TeamVisitQueryParams } from "../../../types/visit";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
  skipData,
  underscoreToCapitalizeString,
} from "../../../utils/utils";
import { VisitStatus, visitTabList } from "./MyVisit";

const { RangePicker } = DatePicker;

enum Status {
  created = "status-warning",
  completed = "status-success",
  reached = "status-info",
  started = "status-info",
  cancelled = "status-danger",
  rescheduled = "status-dark",
}

interface DataType {
  visitDate: string;
  visitTitle: string;
  note: string;
  status: keyof typeof Status;
  _id: string;
}

export type TeamVisitFilterType = Omit<
  TeamVisitQueryParams,
  "limit" | "skip" | "search_keyword"
>;

const searchTypeSelect = visitSearchTypeList?.map((item) => ({
  label: underscoreToCapitalizeString(item),
  value: item,
}));

export default function TeamVisit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { keyword, handleInputChange } = useDebounce();
  const { open, handleClose, handleOpen } = useModal<
    undefined,
    "REPORT_REQUEST"
  >();
  const { page, limit, handlePagination, handleLimit, handleFilter, filter } =
    useTable<TeamVisitFilterType>();

  const { authInfo } = useAppSelector((state) => state.auth);
  const {
    data: teamVisitListData,
    isLoading: isTeamVisitDataLoading,
    isError,
    error,
  } = useGetTeamVisitListQuery({
    params: {
      ...filter,
      search_keyword: keyword,
      limit,
      skip: skipData(page, limit),
    },
  });
  const {
    data: businessData,
    isError: isBusinessError,
    error: businessError,
    isLoading: isBusinessLoading,
  } = useGetBusinessDataQuery(authInfo?.business_id?.toString() || "", {
    skip: !authInfo?.business_id,
  });
  const [
    getVisitReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useGetVisitReportMutation();
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get team visit data fail");
  useError(isBusinessError, businessError, "Get business data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap w-[165px]">Visit Date</div>,
      dataIndex: "schedule_date",
      render: (val) => (val ? formatDate(val) : "n/a"),
      // render: (val) => (val ? formatDateTime(val) : "n/a"),
    },
    {
      title: <div className="text-nowrap">Employee ID</div>,
      dataIndex: "visitor",
      render: (val) => val?.employee_id || "n/a",
    },
    {
      title: <div className="text-nowrap">Employee Name</div>,
      dataIndex: "visitor",
      render: (val) => val?.name || "n/a",
    },
    {
      title: <div className="text-nowrap">Visit Title</div>,
      dataIndex: "title",
      render: (val) => val || "n/a",
    },
    {
      title: <div className="text-nowrap">Description</div>,
      width: 300,
      dataIndex: "description",
      render: (val) => val || "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val: DataType["status"]) => (
        <span className={`${VisitStatus[val]}`}>{capitalize(val)}</span>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (val) => (
        <>
          <Link to={`${location.pathname}/view/${val}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
        </>
      ),
      align: "center",
    },
  ];

  return (
    <SidebarLayout title="Team Visit" tabList={visitTabList}>
      {isBusinessLoading ? (
        <Loader />
      ) : businessData?.data?.business?.is_enable_employee_visit ? (
        <CustomTable
          loading={isTeamVisitDataLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={teamVisitListData?.data.total_visits}
          dataSource={teamVisitListData?.data.visits}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Select
                showSearch
                placeholder="Search Type"
                value={filter?.search_type}
                options={searchTypeSelect}
                onChange={(val) => handleFilter("search_type", val)}
                filterOption={selectSearchOption}
                className="min-w-[120px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              />
              <Input
                suffix={<SearchIcon />}
                placeholder={`Search by ${underscoreToCapitalizeString(
                  filter?.search_type || "name",
                  true
                )}`}
                style={{ maxWidth: 180 }}
                onChange={(val) => {
                  handleInputChange(val);
                }}
              />
              <Select
                allowClear
                showSearch
                placeholder="Department"
                filterOption={selectSearchOption}
                options={selectArrayOption(
                  departmentData?.data?.departments || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                onChange={(val) => {
                  handleFilter("department_id", val);
                }}
              />

              <RangePicker
                onChange={(e) => {
                  handleFilter("start_date", e?.[0]?.format(dbDateFormat));
                  handleFilter("end_date", e?.[1]?.format(dbDateFormat));
                }}
                format={dateFormat}
                style={{ width: 260 }}
                suffixIcon={<DateIcon />}
              />
              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={selectSearchOption}
                options={selectStringOption(
                  Object.keys(Status),
                  "string",
                  true
                )}
                className="min-w-[120px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                onChange={(e) => handleFilter("status", e)}
              />

              <Button
                onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
                className="download-button"
                icon={<DownloadIcon />}
              >
                Report Requests
              </Button>
            </>
          }
        />
      ) : (
        <Empty
          setShowTable={() => navigate("/admin/settings/visit-settings")}
        />
      )}

      {open.type === "REPORT_REQUEST" && (
        <ReportRequestModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          handleSubmit={(params: any) => {
            getVisitReport({
              body: {
                name: "Team visit report",
              },
              params: {
                ...filter,
                skip: 0,
                limit: 0,
                manager_id: authInfo?.id,
                ...params,
              },
            });
          }}
        />
      )}
    </SidebarLayout>
  );
}
